import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, Square, Play } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import VestaFlame from "./VestaFlame";
import { getAs } from "./VestaViewAs";
import { Transcript, SummaryPanel, copyText } from "./VestaVoiceText";
import { MailComposer } from "./VestaMail";
import { VoiceMailCard, RelazioneCard, GuidaCard } from "./VestaVoiceMailCard";
import { voiceStore, useVoiceOpen, getView, takeSeed } from "./vestaStore";


const SILENCE_MS = 2500;
const RATE_KEY = "vesta_voice_rate_v2";
const TEXT_KEY = "vesta_voice_text_v2";
const RATES = [1, 1.25, 1.5, 2];
const MAX_MS = 25000;
const LABEL = { listening: "Ti ascolto…", thinking: "Sto pensando…", speaking: "Vesta sta parlando…", idle: "Tocca il fuoco per parlare", booting: "Un attimo…", preparing: "Vesta ha qualcosa da dirti…" };

const pickMime = () => ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/aac"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
const FOCUS_RX = { batteria: /batteri/g, rete: /\brete\b|prelev|immess|vend/g, consumi: /consum/g, ambiente: /co2|co₂|alberi/g, grafico: /\bmese\b|settiman|\banno\b|grafico|\bieri\b/g, produzione: /produ|\bsole\b|pannell/g };
const SHEET_IDLE_MS = 8000;

// un solo lettore audio per tutta l'app, sbloccato al primo tocco (iOS/Android bloccano l'audio partito senza un gesto)
const AUDIO = typeof Audio !== "undefined" ? new Audio() : null;
const SILENT = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";
let sbloccato = false;
// saluto di Vesta solo alla prima apertura dopo l'avvio dell'app
let salutato = false;
const sblocca = () => {
  if (sbloccato || !AUDIO || !AUDIO.paused || (AUDIO.src && !AUDIO.src.startsWith("data:audio/wav"))) return;
  sbloccato = true;
  AUDIO.src = SILENT;
  AUDIO.play().then(() => { if (AUDIO.currentSrc.startsWith("data:audio/wav")) AUDIO.pause(); }).catch(() => { sbloccato = false; });
};
if (typeof document !== "undefined") ["touchend", "click"].forEach((ev) => document.addEventListener(ev, sblocca, { capture: true }));

// chiude al primo contatto (su iPhone il "click" si perde se il testo sta scorrendo) e ignora il click fantasma sotto
const subito = (fn) => ({
  onPointerDown: (e) => {
    if (e.button > 0) return;
    const stop = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
    document.addEventListener("click", stop, { capture: true, once: true });
    setTimeout(() => document.removeEventListener("click", stop, { capture: true }), 600);
    fn();
  },
  onClick: fn,
});

export function VestaVoiceOverlay({ onClose, src, racconti }) {
  const navigate = useNavigate();
  const navRef = useRef(navigate); navRef.current = navigate;
  const nav = (to) => navRef.current(to);
  const [mini, setMini] = useState(false);
  const [state, setState] = useState("idle");
  const [level, setLevel] = useState(0);
  const [turns, setTurns] = useState([]);
  const [progress, setProgress] = useState(0);
  const [ending] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [mail, setMail] = useState(null);
  const [flow, setFlow] = useState(null);
  const [rel, setRel] = useState(null);
  const [guida, setGuida] = useState(null);
  const [scelta, setScelta] = useState(!!racconti);
  const r = useRef({ sid: "", stream: null, ctx: null, audio: AUDIO, alive: true });
  const [rate, setRateS] = useState(() => Number(localStorage.getItem(RATE_KEY)) || 1.25);
  const [showText, setShowTextS] = useState(() => localStorage.getItem(TEXT_KEY) !== "0");
  const textRef = useRef(showText);
  const setShowText = (v) => { localStorage.setItem(TEXT_KEY, v ? "1" : "0"); textRef.current = v; setShowTextS(v); };
  const setRate = (v) => { localStorage.setItem(RATE_KEY, String(v)); setRateS(v); r.current.audio.playbackRate = v; };

  const [canResume, setCanResume] = useState(false);
  const [tocca, setTocca] = useState(false);
  const play = (b64, da = 0, a1 = 1, from = 0, after = null) => new Promise((res) => {
    const a = r.current.audio;
    if (!b64) { setProgress(a1); res(); return; }
    r.current.cur = { b64, da, a1, after };
    try {
      if ("mediaSession" in navigator && window.MediaMetadata) {
        navigator.mediaSession.metadata = new window.MediaMetadata({ title: "Ai VESTA", artist: src ? "Il tuo impianto" : "Vesta ti risponde", album: "TORRI",
          artwork: [192, 512].map((s) => ({ src: `${window.location.origin}/vesta-icon-${s}.png?v=3`, sizes: `${s}x${s}`, type: "image/png" })) });
      }
    } catch { /* non supportato */ }
    let last = -1, still = 0, done = false;
    const fine = () => { if (done) return; done = true; clearInterval(wd); r.current.attesaTocco = false; setTocca(false); if (url) setTimeout(() => URL.revokeObjectURL(url), 1000); setProgress(a1); res(); };
    // watchdog: se l'audio si blocca (iOS) lo sblocca spostandosi di poco in avanti, poi passa oltre
    const wd = setInterval(() => {
      if (done || r.current.attesaTocco) return;
      if (a.ended) { fine(); return; }
      if (a.currentTime !== last) { last = a.currentTime; still = 0; return; }
      still += 1;
      if (still === 2 || still === 4) { try { if (a.currentTime > 0) a.currentTime += 0.05; } catch { /* */ } a.play().catch(() => {}); }
      if (still >= 7) fine();
    }, 1000);
    let url = null;
    try {
      const bin = atob(b64), u8 = new Uint8Array(bin.length);
      for (let k = 0; k < bin.length; k += 1) u8[k] = bin.charCodeAt(k);
      url = URL.createObjectURL(new Blob([u8], { type: "audio/mpeg" }));
      a.src = url;
    } catch { a.src = `data:audio/mp3;base64,${b64}`; }
    if (from) a.addEventListener("loadedmetadata", () => { try { a.currentTime = Math.min(from, Math.max(0, a.duration - 0.05)); } catch { /* */ } }, { once: true });
    a.preservesPitch = true;
    a.playbackRate = Number(localStorage.getItem(RATE_KEY)) || 1.25;
    a.ontimeupdate = () => a.duration && setProgress(da + (a1 - da) * (a.currentTime / a.duration));
    r.current.endPlay = fine;
    a.onended = fine; a.onerror = fine;
    // audio bloccato dal telefono: invece di saltarlo chiede un tocco per farlo partire
    a.play().catch((e) => { if (e?.name === "NotAllowedError" && !done) { r.current.attesaTocco = true; setTocca(true); } else fine(); });
  });
  const avvia = () => { const a = r.current.audio; r.current.attesaTocco = false; setTocca(false); a.play().then(() => { sbloccato = true; }).catch(() => r.current.endPlay?.()); };
  const playAll = async (list, da, a1) => {
    const arr = [].concat(list || []);
    const tot = arr.reduce((s, b) => s + b.length, 0) || 1;
    let p = da;
    const g = r.current.gen || 0;
    for (let k = 0; k < arr.length; k += 1) {
      if ((r.current.gen || 0) !== g) return;
      const q = p + (a1 - da) * (arr[k].length / tot), rem = arr.slice(k + 1);
      await play(arr[k], p, q, 0, rem.length ? () => playAll(rem, q, a1) : null);
      p = q;
    }
  };

  // Ferma: zittisce subito Vesta, annulla i blocchi audio in arrivo e torna in ascolto
  const skip = () => {
    const S = r.current;
    S.gen = (S.gen || 0) + 1; S.skipped = true; S.started = true; S.skipNovita = true; S.inNovita = false;
    S.resume = S.cur ? { ...S.cur, t: S.audio.currentTime || 0 } : null;
    setCanResume(!!S.resume);
    S.audio.pause(); S.endPlay?.();
    setProgress(1); setConfirm(true);
    S.alive = true; listen();
  };

  // Riprendi: riparte dal punto esatto in cui Vesta era stata fermata, poi torna in ascolto
  const resume = async () => {
    const S = r.current, R = S.resume;
    if (!R) return;
    S.resume = null; setCanResume(false);
    S.alive = false; S.mr?.state === "recording" && S.mr.stop();
    S.gen = (S.gen || 0) + 1;
    const g = S.gen, off = () => (S.gen || 0) !== g;
    setState("speaking");
    await play(R.b64, R.da, R.a1, R.t, R.after);
    if (off()) return;
    await R.after?.();
    if (off()) return;
    S.alive = true; listen();
  };

  const listen = useCallback(async () => {
    const S = r.current;
    if (!S.alive) return;
    S.skipped = false;
    try {
      S.stream = S.stream || await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    } catch { toast.error("Permesso microfono negato"); onClose(); return; }
    S.ctx = S.ctx || new (window.AudioContext || window.webkitAudioContext)();
    const an = S.ctx.createAnalyser(); an.fftSize = 512;
    S.ctx.createMediaStreamSource(S.stream).connect(an);
    const buf = new Uint8Array(an.fftSize);
    const mime = pickMime();
    const mr = new MediaRecorder(S.stream, mime ? { mimeType: mime } : undefined);
    S.mr = mr;
    const chunks = [];
    mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    let spoke = false, quietSince = 0;
    const t0 = Date.now();
    setState("listening");
    mr.start(200);
    const tick = () => {
      if (mr.state !== "recording") return;
      an.getByteTimeDomainData(buf);
      const rms = Math.sqrt(buf.reduce((s, v) => s + ((v - 128) / 128) ** 2, 0) / buf.length);
      setLevel(Math.min(rms * 6, 1));
      const now = Date.now();
      if (rms > 0.04) { spoke = true; quietSince = 0; } else if (spoke && !quietSince) quietSince = now;
      if (!S.alive || (spoke && quietSince && now - quietSince > SILENCE_MS) || now - t0 > MAX_MS || (!spoke && now - t0 > (src ? SHEET_IDLE_MS : 10000))) { mr.stop(); return; }
      requestAnimationFrame(tick);
    };
    mr.onstop = async () => {
      setLevel(0);
      if (!S.alive) return;
      if (!spoke) { setState("idle"); return; }
      S.resume = null; setCanResume(false);
      setState("thinking");
      const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
      const fd = new FormData();
      fd.append("audio", blob, mr.mimeType?.includes("mp4") ? "voce.mp4" : "voce.webm");
      await S.send(fd);
    };
    requestAnimationFrame(tick);
  }, [onClose]); // eslint-disable-line react-hooks/exhaustive-deps

  r.current.send = async (fd) => {
      const S = r.current;
      fd.append("session_id", S.sid || "");
      if (src) fd.append("voce", "true");
      else { const as = getAs(); if (as) fd.append("as", as.id); const v = getView(); if (v) fd.append("vista", JSON.stringify(v)); }
      const gen = S.gen || 0, chiuso = () => (S.gen || 0) !== gen;
      try {
        const res = src ? { data: await src.vesta(fd) } : await api.post("/vesta/voice/turn", fd, { timeout: 90000 });
        if (chiuso()) return;
        S.sid = res.data.session_id;
        const t = res.data;
        setConfirm(!!((t.azione && t.target) || "mail" in t));
        setFlow("mail" in t ? t.mail : null);
        if (t.relazione) setRel(t.relazione);
        setGuida(t.guida || null);
        if ("mail" in t) {}
        else if (t.azione && t.target) {
          if (textRef.current) copyText(t.target.risposta);
        } else setTurns((x) => [...x, { domanda: racconti?.find((x) => x.testo === t.domanda)?.label || t.domanda, risposta: t.risposta, log_id: t.log_id, audio: t.audio, azione: t.azione_ev }].map((y, i, arr) => (i < arr.length - 3 ? { ...y, audio: null } : y)));
        setProgress(0);
        setState("speaking");
        S.skipped = false;
        if (src && t.risposta) {
          const txt = t.risposta.toLowerCase(), score = Object.entries(FOCUS_RX).map(([k, rx]) => [k, (txt.match(rx) || []).length]).sort((a, b) => b[1] - a[1])[0];
          if (score[1]) window.dispatchEvent(new CustomEvent("vesta-focus", { detail: score[0] }));
        }
        const rid = t.audio_resto;
        const blocco = async (i) => {
          for (let k = 0; k < 2; k += 1) {
            try { return src?.resto ? await src.resto(rid, i) : (await api.get(`/vesta/voice/resto/${rid}`, { params: { i }, timeout: 60000 })).data; } catch { /* riprova */ }
          }
          return null;
        };
        const pre = rid ? blocco(0) : null;
        const fr = rid ? (t.audio_frazione || 0.4) : 1;
        const addAudio = (au) => setTurns((x) => x.map((y) => (y.log_id === t.log_id && y.audio ? { ...y, audio: [].concat(y.audio, au) } : y)));
        // streaming: suona ogni blocco appena pronto e intanto prepara il successivo (riavviabile da "Riprendi")
        const streamFrom = async (i0, p0, first) => {
          const g = S.gen || 0, off = () => (S.gen || 0) !== g;
          let p = p0, i = i0, next = first || (rid ? blocco(i0) : null);
          while (next && !off()) {
            let d = await next;
            for (let k = 0; d?.attendi && k < 3 && !off(); k += 1) d = await blocco(i);
            if (!d?.audio || off()) break;
            if (Array.isArray(d.audio)) { addAudio(d.audio); await playAll(d.audio, p, 1); break; }
            const n = d.n || 1, pesi = d.pesi || Array(n).fill(1), tot = pesi.reduce((a, b) => a + b, 0) || 1;
            next = i + 1 < n ? blocco(i + 1) : null;
            const q = Math.min(1, p + (1 - fr) * (pesi[i] / tot)), ni = i + 1;
            addAudio(d.audio);
            await play(d.audio, p, ni >= n ? 1 : q, 0, ni < n ? () => streamFrom(ni, q) : null);
            p = q; i += 1;
          }
        };
        if (t.apri) { play(res.data.audio, 0, 1); minimize(); nav(t.apri.to); return; }
        await play(res.data.audio, 0, fr, 0, t.chiudi || !rid ? null : () => streamFrom(0, fr, pre));
        if (chiuso()) return;
        if (t.chiudi) { S.alive = false; S.stream?.getTracks().forEach((x) => x.stop()); S.stream = null; onClose(); if (!src) nav("/vesta/home"); return; }
        await streamFrom(0, fr, pre);
        if (chiuso()) return;
        if (t.apri && (S.alive || S.skipped)) { minimize(); nav(t.apri.to); return; }
        if (S.alive) listen();
      } catch (e) {
        if (chiuso()) return;
        toast.error(e.response?.data?.detail || "Vesta non è raggiungibile");
        setState("idle");
        if (S.alive) setTimeout(() => S.alive && listen(), 800);
      }
  };

  const replay = async (t) => {
    const S = r.current;
    S.mr?.state === "recording" && (S.alive = false, S.mr.stop());
    setConfirm(true); setState("speaking"); setProgress(0);
    const parti = [].concat(t.audio), g = S.gen || 0;
    for (let i = 0; i < parti.length; i += 1) {
      if ((S.gen || 0) !== g) return;
      const rem = parti.slice(i + 1);
      await play(parti[i], i / parti.length, (i + 1) / parti.length, 0, rem.length ? () => playAll(rem, (i + 1) / parti.length, 1) : null);
    }
    if ((S.gen || 0) !== g) return;
    S.alive = true; setState("idle");
  };
  // Conferma vocale di Vesta dopo che il cliente ha toccato il pulsante azione EV (comando già eseguito)
  const confermaVoce = async (az) => {
    const S = r.current;
    if (!src?.evConferma) { toast.success("Fatto"); return; }
    try {
      S.gen = (S.gen || 0) + 1; S.alive = false; S.resume = null; setCanResume(false);
      S.mr?.state === "recording" && S.mr.stop();
      S.audio.pause(); S.endPlay?.();
      const g = S.gen, off = () => (S.gen || 0) !== g;
      const d = await src.evConferma(az);
      if (off() || !d?.risposta) return;
      setConfirm(true); setProgress(0); setState("speaking");
      setTurns((x) => [...x, { domanda: "", risposta: d.risposta, audio: d.audio, log_id: `ev-${Date.now()}` }]);
      if (d.audio) await play(d.audio, 0, 1);
      if (off()) return;
      setState("idle"); S.alive = true;
    } catch { toast.success("Fatto"); }
  };
  const minimize = () => {
    const S = r.current;
    S.alive = false; S.mr?.state === "recording" && S.mr.stop();
    S.stream?.getTracks().forEach((x) => x.stop()); S.stream = null;
    setState("idle"); setMini(true);
  };
  const restore = () => { setMini(false); r.current.alive = true; listen(); };
  const scegli = (x) => {
    const S = r.current;
    setScelta(false); S.alive = true; S.started = true; setState("thinking");
    const fd = new FormData(); fd.append("testo", x.testo);
    S.send(fd);
  };
  const close = () => {
    const S = r.current;
    S.gen = (S.gen || 0) + 1; S.alive = false; S.skipped = true; S.resume = null;
    S.audio.pause(); S.endPlay?.(); S.mr?.state === "recording" && S.mr.stop();
    S.stream?.getTracks().forEach((x) => x.stop()); S.stream = null;
    setState("idle"); onClose();
  };
  r.current.closeRef = close;

  useEffect(() => {
    const S = r.current;
    S.alive = true;
    S.boot = S.boot || (async () => {
      if (src) {
        // aperta dalla "i" della fascia Vesta: si presenta subito, senza saluto
        if (racconti) { S.started = true; return; }
        if (!src.saluto || salutato) return;
        salutato = true;
        try {
          setState("preparing");
          const g = await src.saluto();
          if (!S.alive || S.skipNovita) return;
          setTurns([{ domanda: "", risposta: g.testo, audio: g.audio, log_id: "saluto" }]);
          setConfirm(true); setState("speaking"); await play(g.audio);
        } catch { /* niente saluto */ }
        return;
      }
      const seed = takeSeed();
      if (seed) {
        S.started = true; setState("thinking");
        const fd = new FormData(); fd.append("testo", seed);
        await S.send(fd);
        return;
      }
      try {
        setState("booting");
        const c = await api.get("/vesta/voice/novita", { params: { check: 1 } });
        if (!c.data.ha || S.skipNovita) return;
        setState("preparing");
        const n = await api.get("/vesta/voice/novita", { timeout: 60000 });
        if (!n.data.testo || S.skipNovita) return;
        api.post("/vesta/voice/novita/dette", { ids: n.data.ids, presentazione: n.data.presentazione }).catch(() => {});
        S.inNovita = true;
        if (n.data.audio) { setConfirm(true); setState("speaking"); await play(n.data.audio); } else toast(n.data.testo, { duration: 6000, position: "bottom-center" });
        S.inNovita = false;
      } catch { /* nessuna novità */ }
      if (!S.skipNovita) setState("idle");
    })();
    S.boot.then(() => { if (S.alive && !S.started) { S.started = true; listen(); } });
    return () => { S.gen = (S.gen || 0) + 1; S.alive = false; S.audio.pause(); S.endPlay?.(); S.mr?.state === "recording" && S.mr.stop(); S.stream?.getTracks().forEach((t) => t.stop()); S.ctx?.close?.(); S.stream = null; S.ctx = null; };
  }, [listen]); // eslint-disable-line react-hooks/exhaustive-deps

  const keep = { onPointerDown: (e) => e.stopPropagation(), style: { pointerEvents: "auto" } };
  // maniglia: trascinando si alza/abbassa la scheda (sotto il 30% si chiude)
  const drag = useRef(null);
  const [alto, setAlto] = useState(62);
  const altoDa = (y) => Math.min(94, Math.max(15, ((window.innerHeight - y) / window.innerHeight) * 100));
  const sheetTouch = {
    onPointerDown: (e) => { e.currentTarget.setPointerCapture?.(e.pointerId); drag.current = { y: e.clientY, h: alto }; },
    onPointerMove: (e) => { if (drag.current) setAlto(altoDa(e.clientY)); },
    onPointerUp: (e) => {
      if (!drag.current) return;
      const h = altoDa(e.clientY), mosso = Math.abs(e.clientY - drag.current.y) > 8, da = drag.current.h;
      drag.current = null;
      if (!mosso) { setAlto(da < 78 ? 92 : 62); return; }
      if (h < 30) { close(); return; }
      setAlto(h > 77 ? 92 : h > 46 ? 62 : 40);
    },
    onPointerCancel: () => { drag.current = null; },
  };
  if (mini) return (
    <div className="fixed bottom-5 right-5 z-[90] flex items-center gap-1 rounded-full border border-[#C17F59]/50 bg-black/90 py-1 pl-1 pr-2 shadow-[0_0_30px_-6px_rgba(193,127,89,.6)] backdrop-blur" data-testid="vesta-voice-mini" {...keep}>
      <button type="button" onClick={restore} className="flex items-center gap-1.5 transition-transform hover:scale-105" title="Riprendi a parlare con Vesta" data-testid="vesta-voice-mini-resume">
        <VestaFlame size={46} /><span className="pr-1 text-[11px] font-semibold text-[#E8B48F]">Parla con Vesta</span>
      </button>
      <button type="button" {...subito(onClose)} className="touch-manipulation rounded-full p-1 text-neutral-500 hover:text-white" title="Chiudi Vesta" data-testid="vesta-voice-mini-close"><X className="h-4 w-4" /></button>
    </div>
  );
  return (
    <div {...keep} className={src ? "fixed inset-0 z-[80]" : "fixed inset-0 z-[80] bg-black/95 backdrop-blur-xl"} data-testid="vesta-voice-overlay">
      {src && <button type="button" aria-label="Chiudi Vesta" onClick={close} style={{ height: `${100 - alto}dvh` }} className="absolute inset-x-0 top-0 bg-transparent" data-testid="vesta-sheet-backdrop" />}
      <div style={src ? { height: `${alto}dvh`, transition: drag.current ? "none" : "height .25s ease-out" } : undefined} className={src ? "absolute inset-x-0 bottom-0 overflow-hidden rounded-t-[2rem] bg-black shadow-[0_-10px_40px_rgba(0,0,0,.35)] animate-[vsUp_.35s_ease-out]" : "absolute inset-0"} data-testid={src ? "vesta-sheet" : undefined}>
      {src && <style>{"@keyframes vsUp{from{transform:translateY(100%)}to{transform:none}}"}</style>}
      {src && <div {...sheetTouch} className="absolute inset-x-0 top-0 z-20 flex h-10 cursor-grab touch-none items-center justify-center" data-testid="vesta-sheet-handle"><span className="h-1.5 w-14 rounded-full bg-neutral-600" /></div>}
      {src && <button type="button" {...subito(close)} aria-label="Chiudi Vesta" className="touch-manipulation absolute right-3 top-3 z-30 rounded-full bg-white/10 p-2 text-white ring-1 ring-white/20 transition-colors hover:bg-white/20" data-testid="vesta-voice-close"><X className="h-6 w-6" strokeWidth={2.5} /></button>}
      <div className={`absolute inset-0 flex flex-col items-center overflow-y-auto overscroll-contain px-6 [&>*]:shrink-0 [-webkit-overflow-scrolling:touch] ${src ? "pb-44 pt-10" : "pb-28 pt-16"}`} data-testid="vesta-voice-scroll">
      <div className="mt-auto" />
      {!src && <button type="button" {...subito(close)} aria-label="Chiudi Vesta" className="touch-manipulation fixed right-4 top-4 z-30 rounded-full bg-white/10 p-3.5 text-white ring-1 ring-white/20 transition-colors hover:bg-white/20" data-testid="vesta-voice-close"><X className="h-9 w-9" strokeWidth={2.5} /></button>}
      <div className={`sticky z-10 flex w-full flex-col items-center ${src ? "top-10 bg-black pb-2" : "top-0 bg-black/95 pb-3 backdrop-blur-sm"}`} data-testid="vesta-voice-head">
      <button type="button" onClick={() => {
        const S = r.current;
        if (state === "booting" || state === "preparing") { S.skipNovita = true; S.started = true; S.alive = true; listen(); }
        else if (state === "speaking" && tocca) avvia();
        else if (state === "speaking") skip();
        else if (state === "idle" && !ending) { S.alive = true; listen(); }
      }} className="transition-transform active:scale-95" data-testid="vesta-voice-flame" title={state === "speaking" ? "Interrompi Vesta" : undefined}>
        <VestaFlame size={src ? (turns.length ? 84 : 120) : turns.length ? 140 : 190} level={level} state={state === "booting" || state === "preparing" ? "thinking" : state} />
      </button>
      <p className={`${src ? "mt-3" : "mt-6"} text-sm font-semibold tracking-wide text-[#E8B48F]`} data-testid="vesta-voice-state">{ending ? "Conversazione terminata" : LABEL[state]}</p>
      </div>
      {scelta && state === "idle" && (
        <div className="mt-4 grid w-full max-w-sm grid-cols-2 gap-2" data-testid="vesta-racconti">
          <p className="col-span-2 text-center text-xs uppercase tracking-widest text-neutral-500">Cosa vuoi ascoltare?</p>
          {racconti.map((x) => (
            <button key={x.id} type="button" onClick={() => scegli(x)} data-testid={`vesta-racconto-${x.id}`}
              className="rounded-full border border-[#C17F59]/60 px-3 py-2.5 text-[13px] font-semibold text-[#E8B48F] transition-colors hover:bg-[#C17F59]/20 active:bg-[#C17F59]/30">{x.label}</button>
          ))}
        </div>
      )}
      {state === "speaking" && tocca && (
        <button type="button" onClick={avvia} data-testid="vesta-voice-tap-audio"
          className="fixed bottom-56 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full bg-[#C17F59] px-6 py-3 text-sm font-bold uppercase tracking-widest text-black shadow-lg"><Play className="h-4 w-4 fill-current" />Tocca per ascoltare</button>
      )}
      {state === "speaking" && (
        <button type="button" onClick={skip} data-testid="vesta-voice-skip"
          className="fixed bottom-24 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full border border-[#C17F59] bg-black/80 px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#E8B48F] transition-colors hover:bg-[#C17F59]/20"><Square className="h-3 w-3 fill-current" />Ferma</button>
      )}
      {canResume && state !== "speaking" && state !== "thinking" && (
        <button type="button" onClick={resume} data-testid="vesta-voice-resume"
          className="fixed bottom-24 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full border border-[#C17F59] bg-[#C17F59] px-5 py-2 text-xs font-bold uppercase tracking-widest text-black transition-colors hover:bg-[#E8B48F]"><Play className="h-3 w-3 fill-current" />Riprendi</button>
      )}
      {state === "listening" && (
        <button type="button" onClick={() => r.current.mr?.state === "recording" && r.current.mr.stop()} data-testid="vesta-voice-done"
          className="mt-5 rounded-full border border-[#C17F59] px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#E8B48F] transition-colors hover:bg-[#C17F59]/20">Ho finito</button>
      )}
      <div className="fixed bottom-6 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1 rounded-full border border-neutral-800 bg-black/90 p-1 backdrop-blur" data-testid="vesta-voice-speed">
        <span className="px-2 text-[10px] uppercase tracking-widest text-neutral-500">Velocità</span>
        {RATES.map((v) => (
          <button key={v} type="button" onClick={() => setRate(v)} data-testid={`vesta-voice-speed-${v}`}
            className={`rounded-full px-3 py-1 font-mono text-xs transition-colors ${rate === v ? "bg-[#C17F59] text-black" : "text-neutral-400 hover:text-[#E8B48F]"}`}>{`x${String(v).replace(".", ",")}`}</button>
        ))}
        <span className="mx-1 h-4 w-px bg-neutral-800" />
        <button type="button" onClick={() => setShowText(!showText)} data-testid="vesta-voice-text-toggle" aria-pressed={showText}
          className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors ${showText ? "bg-[#C17F59] text-black" : "text-neutral-400 hover:text-[#E8B48F]"}`}>Testo</button>
      </div>
      {!showText ? null : ending ? <SummaryPanel turns={turns} onClose={onClose} onMail={src ? undefined : setMail} /> : <Transcript turns={turns} progress={progress} speaking={state === "speaking" && !confirm} onReplay={replay} onMail={src ? undefined : setMail} piena={!!src} src={src} onConferma={confermaVoce} />}
      {showText && flow && <VoiceMailCard flow={flow} />}
      {showText && !flow && guida && <GuidaCard g={guida} />}
      {showText && !flow && !guida && rel && <RelazioneCard rel={rel} />}
      {mail && <MailComposer draft={mail} onClose={() => setMail(null)} />}
      <div className="mb-auto" />
      </div>
      </div>
    </div>
  );
}

export function VestaVoiceHost() {
  const open = useVoiceOpen();
  const { pathname } = useLocation();
  return open && pathname.startsWith("/vesta/") ? <VestaVoiceOverlay onClose={voiceStore.close} /> : null;
}

export default function VestaVoice() {
  return (
    <>
      <span className="inline-flex flex-col items-end">
        <button type="button" onClick={voiceStore.open} className="group inline-flex items-center gap-1.5" data-testid="vesta-voice-btn" title="Parla con Vesta">
          <span className="transition-transform group-hover:scale-110"><VestaFlame size={30} /></span>
          <span className="text-[11px] font-medium text-neutral-500 transition-colors group-hover:text-[#E8B48F]">parla con Vesta</span>
        </button>
      </span>
    </>
  );
}
